import { roomsIntersect } from "./RoomGenerator";
import {
  TileType,
  manhattanDistance,
  pointKey,
  type LevelData,
  type Room,
  type ValidationResult,
} from "./types";

export class LevelValidator {
  public validate(level: LevelData): ValidationResult {
    const errors: string[] = [];
    const reachable = level.grid.floodFill(level.playerStart);

    if (!level.grid.isWalkable(level.playerStart)) {
      errors.push("Player start is blocked");
    }
    if (!reachable.has(pointKey(level.exit))) {
      errors.push("Exit is unreachable");
    }

    const finale = level.rooms.find((room) => room.id === "finale");
    if (!finale || pointKey(level.exit) !== pointKey(finale.center)) {
      errors.push("Exit is not centered in the finale room");
    }
    const proceduralBosses = level.enemies.filter((enemy) => enemy.kind === "abyssTyrant");
    if (proceduralBosses.length !== 1 || proceduralBosses[0]?.roomId !== "finale") {
      errors.push("Procedural finale must contain exactly one Abyss Tyrant");
    }

    for (const room of level.rooms.filter((candidate) => candidate.required)) {
      if (!reachable.has(pointKey(room.center))) {
        errors.push(`Required room ${room.id} is unreachable`);
      }
    }

    for (let firstIndex = 0; firstIndex < level.rooms.length; firstIndex += 1) {
      for (let secondIndex = firstIndex + 1; secondIndex < level.rooms.length; secondIndex += 1) {
        const first = level.rooms[firstIndex];
        const second = level.rooms[secondIndex];
        if (first && second && roomsIntersect(first, second, 0)) {
          errors.push(`Rooms ${first.id} and ${second.id} overlap`);
        }
      }
    }

    for (const pickup of level.pickups) {
      if (!level.grid.isWalkable(pickup.cell)) {
        errors.push(`Pickup ${pickup.id} is inside a wall`);
      }
    }

    for (const enemy of level.enemies) {
      if (!level.grid.isWalkable(enemy.cell)) {
        errors.push(`Enemy ${enemy.id} is inside a wall`);
      }
      if (manhattanDistance(enemy.cell, level.playerStart) < 8) {
        errors.push(`Enemy ${enemy.id} is too close to the player start`);
      }
    }

    if (!level.rooms.some((room) => room.id === level.secretRoomId)) {
      errors.push("Secret room is missing");
    }
    if (!level.doors.some((door) => door.secret)) {
      errors.push("Secret door is missing");
    }
    for (const door of level.doors) {
      const edge = level.graph.edges.find(
        (candidate) => door.id === `${candidate.from}-${candidate.to}`,
      );
      const targetRoom = level.rooms.find((room) => room.id === edge?.to);
      if (!targetRoom || !door.cells.every((cell) => isRoomThreshold(cell, targetRoom))) {
        errors.push(`Door ${door.id} is not on its target room threshold`);
      }
      const first = door.cells[0]!;
      const last = door.cells[door.cells.length - 1]!;
      const side = door.orientation === "x" ? { x: 1, y: 0 } : { x: 0, y: 1 };
      if (
        level.grid.get({ x: first.x - side.x, y: first.y - side.y }) !== TileType.Wall ||
        level.grid.get({ x: last.x + side.x, y: last.y + side.y }) !== TileType.Wall
      ) {
        errors.push(`Door ${door.id} has no solid jambs`);
      }
      const expectedTile = door.secret ? TileType.SecretDoor : TileType.Door;
      if (door.cells.some((cell) => level.grid.get(cell) !== expectedTile)) {
        errors.push(`Door ${door.id} does not block its full corridor footprint`);
      }
    }

    // Open ordinary doors, but keep the secret sealed: no accidental corridor may bypass it.
    const secretDoorCells = level.doors.filter((door) => door.secret).flatMap((door) => door.cells);
    const savedTiles = secretDoorCells.map((cell) => level.grid.get(cell));
    let withoutSecrets: Set<string>;
    try {
      for (const cell of secretDoorCells) level.grid.set(cell, TileType.Wall);
      withoutSecrets = level.grid.floodFill(level.playerStart);
    } finally {
      secretDoorCells.forEach((cell, index) => level.grid.set(cell, savedTiles[index]!));
    }
    const secretRoom = level.rooms.find((room) => room.id === level.secretRoomId);
    if (secretRoom && withoutSecrets.has(pointKey(secretRoom.center)))
      errors.push("Secret room can be bypassed");
    for (const room of level.rooms.filter((room) => room.required)) {
      if (!withoutSecrets.has(pointKey(room.center)))
        errors.push(`Required route depends on secret ${room.id}`);
    }

    const walkableCount = level.grid.walkableCells().length;
    if (reachable.size !== walkableCount) {
      errors.push(`Level contains ${walkableCount - reachable.size} isolated walkable cells`);
    }

    const minimumRoomX = Math.min(...level.rooms.map((room) => room.x));
    const minimumRoomY = Math.min(...level.rooms.map((room) => room.y));
    const maximumRoomX = Math.max(...level.rooms.map((room) => room.x + room.width));
    const maximumRoomY = Math.max(...level.rooms.map((room) => room.y + room.height));
    if (minimumRoomX > 2 || minimumRoomY > 2 || maximumRoomX < 62 || maximumRoomY < 62) {
      errors.push("Procedural rooms do not span the usable 64x64 grid");
    }

    return { valid: errors.length === 0, errors };
  }
}

function isRoomThreshold(point: { readonly x: number; readonly y: number }, room: Room): boolean {
  return (
    ((point.x === room.x - 1 || point.x === room.x + room.width) &&
      point.y >= room.y &&
      point.y < room.y + room.height) ||
    ((point.y === room.y - 1 || point.y === room.y + room.height) &&
      point.x >= room.x &&
      point.x < room.x + room.width)
  );
}
